import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from '../../utils/axios';
import { usePageContext } from '../../context/PageContext/PageContext';
import BulkPhotosModal from '../../components/BulkPhotosModal/BulkPhotosModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import useConfirmModal from '../../components/ConfirmModal/useConfirmModal';
import useLeaseGate from '../../components/LeaseAgreementGate/useLeaseGate';
import LeaseAgreementGate from '../../components/LeaseAgreementGate/LeaseAgreementGate';
import PropertyGroup from './PropertyGroup';
import PropertyDrawer from './Drawer/PropertyDrawer';
import { enrichListing, groupListings, listingMatchesTab, PROPS_TABS, bulkDraftReady } from './groupings';
import { routeForRow } from './routing';
import { confirmAllAi } from '../BulkUpload/bulkApi';
import { toastInfo, toastSuccess, toastError } from '../../utils/toast';
import './MyProperties.css';

export default function MyPropertiesPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { listingId: drawerListingId } = useParams();
    const { setPageTitle } = usePageContext();
    const confirmModal = useConfirmModal();
    const leaseGate = useLeaseGate();
    const bulkGroupRef = useRef(null);

    const [apiListings, setApiListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [bulkSubmitting, setBulkSubmitting] = useState(false);
    const [bulkPhotosOpen, setBulkPhotosOpen] = useState(false);

    const [fetchedDrawerListing, setFetchedDrawerListing] = useState(undefined);

    const fetchListings = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setError(null);
        try {
            const res = await axios.get('/listings');
            setApiListings(res.data.listings || []);
        } catch {
            setError('Failed to load properties. Please try again.');
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        setPageTitle('My Properties');
    }, [setPageTitle]);

    useEffect(() => {
        fetchListings();
    }, [fetchListings]);

    const listings = useMemo(() => apiListings, [apiListings]);

    const groups = useMemo(
        () => groupListings(listings, activeTab),
        [listings, activeTab],
    );

    const tabCounts = useMemo(() => {
        const enriched = listings.map(enrichListing).filter(Boolean);
        const counts = { all: enriched.length };
        for (const tab of PROPS_TABS) {
            if (tab.key === 'all') continue;
            // PP-403 — listingMatchesTab handles tabs that combine
            // multiple _v5Status values (Paused includes paused +
            // paused_review).
            counts[tab.key] = enriched.filter((l) => listingMatchesTab(l, tab.key)).length;
        }
        return counts;
    }, [listings]);

    const bulkJobIdForGroup = useMemo(() => {
        const bulkGroup = groups.find((g) => g.key === 'bulk_draft');
        if (!bulkGroup?.rows?.length) return null;
        return bulkGroup.rows[0].bulkJobId || null;
    }, [groups]);

    const bulkDrafts = useMemo(() => {
        const bulkGroup = groups.find((g) => g.key === 'bulk_draft');
        return bulkGroup?.rows ?? [];
    }, [groups]);

    useEffect(() => {
        if (location.state?.scrollToBulk && bulkGroupRef.current) {
            bulkGroupRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, groups, navigate]);

    const drawerListingFromList = useMemo(() => {
        if (!drawerListingId) return null;
        const enriched = listings.map(enrichListing).filter(Boolean);
        return enriched.find((l) => l._id === drawerListingId) ?? undefined;
    }, [drawerListingId, listings]);

    useEffect(() => {
        if (!drawerListingId || drawerListingFromList !== undefined) {
            setFetchedDrawerListing(undefined);
            return undefined;
        }
        let cancelled = false;
        setFetchedDrawerListing(undefined);
        axios.get(`/listings/${drawerListingId}`)
            .then((res) => {
                if (!cancelled) {
                    setFetchedDrawerListing(enrichListing(res.data.listing) || null);
                }
            })
            .catch(() => {
                if (!cancelled) setFetchedDrawerListing(null);
            });
        return () => { cancelled = true; };
    }, [drawerListingId, drawerListingFromList]);

    const resolvedDrawerListing = drawerListingId
        ? (drawerListingFromList ?? fetchedDrawerListing ?? undefined)
        : null;
    const showDrawer = Boolean(drawerListingId);
    const drawerReady = !showDrawer || resolvedDrawerListing !== undefined;

    const isEmpty = !loading && apiListings.length === 0;

    const handleRowNavigate = (listing) => {
        navigate(routeForRow(listing));
    };

    const handleRowNavigateEdit = (listing) => {
        navigate(routeForRow(listing, { edit: true }));
    };

    const handleBulkUpload = () => {
        navigate('/dashboard/listings/bulk');
    };

    const handleAddProperty = () => {
        navigate('/dashboard/listings/new/1');
    };

    const handleConfirmAllAi = () => {
        if (!bulkJobIdForGroup) return;
        confirmModal.confirm({
            title: 'Confirm all AI fields?',
            message: 'This will mark every pending AI field as confirmed across all drafts in this batch. You can still edit individual fields after.',
            confirmLabel: 'Confirm all',
            cancelLabel: 'Cancel',
            variant: 'default',
            onConfirm: async () => {
                try {
                    const res = await confirmAllAi(bulkJobIdForGroup);
                    toastSuccess(res.skipped > 0
                        ? `Confirmed AI fields on ${res.updated} drafts. ${res.skipped} need manual review — AI enrichment failed for those.`
                        : `Confirmed AI fields on ${res.updated} drafts.`);
                    await fetchListings({ silent: true });
                } catch {
                    toastError('Couldn\'t confirm AI fields. Please try again.');
                }
            },
        });
    };

    const handlePlaceholderAction = (action) => {
        if (action === 'confirm-ai') {
            handleConfirmAllAi();
            return;
        }
        if (action === 'defaults') {
            toastInfo('Set defaults for all drafts — coming soon.');
            return;
        }
    };

    const proceedWithBulkSubmit = async (readyListings) => {
        if (!readyListings.length || bulkSubmitting) return;
        setBulkSubmitting(true);
        try {
            await leaseGate.runWithLeaseGate({
                listings: readyListings,
                onSuccess: async () => {
                    await fetchListings({ silent: true });
                },
                onError: () => {
                    setError('Bulk submit failed. Please try again.');
                },
            });
        } catch {
            setError('Bulk submit failed. Please try again.');
        } finally {
            setBulkSubmitting(false);
        }
    };

    const handleBulkSubmit = (readyListings) => {
        if (!readyListings.length || bulkSubmitting) return;
        proceedWithBulkSubmit(readyListings);
    };

    const showToolbar = !loading && !isEmpty;

    return (
        <div className="my-properties-page">
            <div className="page-body">
                <div className="props-heading-row">
                    <div>
                        <h1 className="page-heading">My Properties</h1>
                        <p className="page-sub">
                            View the status of your properties, manage listings, and respond to any requested changes.
                        </p>
                    </div>
                    {showToolbar && (
                        <div className="props-heading-actions">
                            <button type="button" className="btn btn-secondary" onClick={handleBulkUpload}>
                                Bulk upload
                            </button>
                            <button type="button" className="btn btn-primary" onClick={handleAddProperty}>
                                + Add property
                            </button>
                        </div>
                    )}
                </div>

                {showToolbar && (
                    <div className="props-toolbar">
                        <div className="props-tabs" role="tablist" aria-label="Filter listings by status">
                            {PROPS_TABS.map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === tab.key}
                                    className={`props-tab${activeTab === tab.key ? ' active' : ''}`}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.label}
                                    <span className="tab-count">{tabCounts[tab.key] ?? 0}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {loading && (
                    <div className="my-properties-loading">Loading properties…</div>
                )}

                {error && !loading && (
                    <div className="my-properties-error">
                        <span>{error}</span>
                        <button type="button" className="btn btn-secondary" onClick={fetchListings}>
                            Retry
                        </button>
                    </div>
                )}

                {isEmpty && !loading && (
                    <div className="my-properties-empty">
                        <h2>You haven&apos;t added any properties yet</h2>
                        <p>Add a single property or upload many at once to get started.</p>
                        <div className="my-properties-empty-actions">
                            <button type="button" className="btn btn-primary" onClick={handleAddProperty}>
                                + Add property
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={handleBulkUpload}>
                                Bulk upload
                            </button>
                        </div>
                    </div>
                )}

                {!loading && !isEmpty && (
                    <div className="props-list">
                        {groups.map((group) => (
                            <PropertyGroup
                                key={group.key}
                                group={group}
                                rows={group.rows}
                                groupRef={group.key === 'bulk_draft' ? bulkGroupRef : undefined}
                                onRowNavigate={handleRowNavigate}
                                onRowNavigateEdit={handleRowNavigateEdit}
                                onBulkSubmit={handleBulkSubmit}
                                bulkSubmitting={bulkSubmitting}
                                onPlaceholderAction={handlePlaceholderAction}
                                onBulkAddPhotos={() => setBulkPhotosOpen(true)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {showDrawer && drawerReady && (
                <PropertyDrawer
                    listing={resolvedDrawerListing}
                    bulkDrafts={bulkDrafts}
                    onSubmitted={() => fetchListings({ silent: true })}
                    onListingUpdated={(updated) => {
                        setApiListings((prev) => prev.map((l) => (
                            l._id === updated._id ? updated : l
                        )));
                        setFetchedDrawerListing((prev) => (
                            prev?._id === updated._id ? enrichListing(updated) : prev
                        ));
                    }}
                />
            )}

            {bulkPhotosOpen && (
                <BulkPhotosModal
                    open={bulkPhotosOpen}
                    bulkDrafts={bulkDrafts}
                    onClose={() => setBulkPhotosOpen(false)}
                    onSaved={() => fetchListings({ silent: true })}
                />
            )}

            <ConfirmModal {...confirmModal.props} />
            <LeaseAgreementGate {...leaseGate.gateProps} />
        </div>
    );
}
